import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";
import type { ReactNode } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { pageView } from "@/lib/analytics";
import { reportVibeError } from "@/lib/vibe-error-reporting";
import appCss from "../styles.css?url";

const trackingParams = [
  "fbclid",
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "a",
];

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="es-AR" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const captureAndTrack = (toLocation: {
      pathname: string;
      searchStr: string;
    }) => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }

      const params = new URLSearchParams(toLocation.searchStr);
      for (const key of trackingParams) {
        const value = params.get(key);
        if (value) localStorage.setItem(key, value);
      }
      localStorage.setItem("url_origen", window.location.href);
      pageView(`${toLocation.pathname}${toLocation.searchStr}`, document.title);
    };

    captureAndTrack(router.state.location);

    const unsubscribe = router.subscribe("onResolved", ({ toLocation }) => {
      captureAndTrack(toLocation);

      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });

    return unsubscribe;
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <Sonner />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootNotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-6">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold">404</h1>
        <p className="mb-4 text-xl text-muted-foreground">
          La página que buscás no existe.
        </p>
        <Link to="/" className="text-primary underline hover:text-primary/90">
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

function RootError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportVibeError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <div className="max-w-md text-center">
        <h1 className="mb-3 text-3xl font-bold">This page didn&apos;t load</h1>
        <p className="mb-6 text-muted-foreground">
          Intentá de nuevo o volvé al inicio.
        </p>
        <div className="flex justify-center gap-3">
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <Link
            to="/"
            className="rounded-md border border-border px-4 py-2 font-medium"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1.0" },
        { name: "author", content: "crecelocal" },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        { rel: "icon", type: "image/png", href: "/favicon.svg" },
        {
          rel: "preload",
          as: "image",
          type: "image/webp",
          href: "https://vibe.filesafe.space/1775608735188032431/assets/e5359df8-6638-4410-bd70-61fc1fc9a3fb.webp",
          fetchPriority: "high",
        },
        {
          rel: "preconnect",
          href: "https://fonts.googleapis.com",
        },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Geist+Mono:wght@400;500&family=Geist:wght@400;500&display=swap",
        },
      ],
      scripts: [
        {
          children: `!(function (f, b, e, v, n, t, s) { if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); }; if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = []; t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s); })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js"); fbq("set", "autoConfig", false, "4425487397729356"); fbq("init", "4425487397729356"); fbq("track", "PageView");`,
          async: true,
        },
        {
          src: "https://www.googletagmanager.com/gtag/js?id=G-XJS0GQJH78",
          async: true,
          defer: true,
        },
        {
          children: `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag("js", new Date()); gtag("config", "G-XJS0GQJH78", { send_page_view: false });`,
        },
        {
          children: `(function () { var t = document.createElement("script"); t.type = "text/javascript"; t.async = !0; t.defer = !0; t.src = "https://link.crecelocal.com/js/am.js"; t.onload = t.onreadystatechange = function () { var state = this.readyState; if (!state || state === "complete" || state === "loaded") try { affiliateManager.init("UmeUICnWy44Ung9XrMjl", "https://backend.leadconnectorhq.com", ".crecelocal.com"); } catch (error) {} }; var first = document.getElementsByTagName("script")[0]; first.parentNode.insertBefore(t, first); })();`,
        },
      ],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: RootNotFound,
    errorComponent: RootError,
  },
);
